import React, { useState, useContext, useEffect } from 'react';
import { useMutation } from '@apollo/client';
import { css } from '@emotion/react';
import {
  Flex,
  Box,
  Panel,
  Link,
  Text,
  theme,
  Modal,
  Button,
} from '@xometry/xometry_loft';
import TeamDetailsEditPanel from './EditPanel';
import TeamManagementKebabMenu from './TeamManagementKebabMenu';
import {
  Organization,
  UpdateOrganizationIsArchivedQuery,
  UpdateOrganizationIsArchivedMutationResult,
  UpdateOrganizationIsArchivedMutationVariables,
} from '../../../../schema/generated/schema';
import NotificationContext from '../../../../contexts/Notification';
import quotesApi from '../../../../services/quotes';

const TeamInfoPanel = ({
  org,
  isStaff,
  hasWriteOrgPermission,
}: {
  isStaff: boolean;
  org: Organization | null;
  hasWriteOrgPermission?: boolean;
}) => {
  const [confirmArchivedTeam, setConfirmArchivedTeam] = useState(false);
  const [isArchivedModalOpen, setIsArchivedModalOpen] = useState(false);

  const { addNotification } = useContext(NotificationContext);

  const [updateOrganizationIsArchivedMutation] = useMutation<
    UpdateOrganizationIsArchivedMutationResult,
    UpdateOrganizationIsArchivedMutationVariables
  >(UpdateOrganizationIsArchivedQuery);
  const isUserAllowed = isStaff || hasWriteOrgPermission;

  useEffect(() => {
    if (confirmArchivedTeam) {
      setConfirmArchivedTeam(false);
      if (isUserAllowed) {
        updateOrganizationIsArchivedMutation({
          variables: {
            orgId: org?.id,
            isArchived: true,
          },
          onCompleted: () => {
            addNotification(
              'success',
              `${org?.name} was deactivated and can be accessed from your account settings.`
            );
            // Get a new token without the deactivated org
            quotesApi.refreshAuthToken();
          },
          onError: (error: any) => {
            console.error(error);
            addNotification(
              'danger',
              'There was an error deactivating your team.'
            );
          },
        });
      }
    }
  }, [confirmArchivedTeam]);

  const [editMode, setEditMode] = useState(false);

  const toggleEditMode = () => {
    setEditMode(!editMode);
  };

  return (
    <Box color={theme.colors.darkBlues[100]}>
      <Flex
        marginBottom={theme.space[4]}
        justifyContent="space-between"
        alignItems="center"
      >
        <Text kind="subpage-title">{org?.name}</Text>
      </Flex>
      <Panel kind="primary" lineHeight="24px">
        <Flex
          alignItems="flex-start"
          alignSelf="stretch"
          css={css`
            gap: 12px;
          `}
        >
          <Flex
            flex="1 0 0"
            flexDirection="column"
            fontSize={theme.fontSizes[2]}
            css={css`
              font-weight: 600;
            `}
          >
            {editMode && `Edit `}Team Name
          </Flex>
          <Flex flex="1 0 0" flexDirection="column">
            <Flex justifyContent="flex-end" alignItems="flex-start">
              {!org?.isArchived && isUserAllowed && (
                <TeamManagementKebabMenu
                  handleEdit={toggleEditMode}
                  handleArchivingTeam={() => {
                    setIsArchivedModalOpen(true);
                  }}
                  canEdit={isUserAllowed}
                  editMode={!editMode}
                />
              )}
            </Flex>
          </Flex>
        </Flex>
        {editMode ? (
          <TeamDetailsEditPanel
            orgId={org?.id}
            orgName={org?.name}
            isStaff={isStaff}
            setTeamDetailsEditPanelOpen={setEditMode}
          />
        ) : (
          <Flex
            fontSize={theme.fontSizes[2]}
            css={css`
              font-weight: 400;
              padding-top: 10px;
            `}
          >
            {org?.name}
          </Flex>
        )}
      </Panel>
      {isArchivedModalOpen && (
        <Modal
          isOpen={isArchivedModalOpen}
          padding="40px"
          onClose={() => setIsArchivedModalOpen(false)}
          breakpointMaxWidth="734px"
        >
          <Flex justifyContent="space-between">
            <Flex
              lineHeight="44px"
              color={theme.colors.darkBlues[100]}
              fontSize={theme.fontSizes[4]}
              justifyContent="left"
              marginBottom={theme.space[3]}
            >
              Are you sure you want to deactivate {org?.name}?
            </Flex>
            <Modal.CloseButton />
          </Flex>
          <Flex>
            <Box flexBasis="100%">
              <Text display="block" marginBottom={theme.space[4]}>
                By deactivating the team{' '}
                <Text
                  css={css`
                    font-weight: 550;
                  `}
                >
                  {org?.name}
                </Text>
                , you will still have access to all quotes and orders associated
                to this team made by you and {org?.activeMembers} other team
                members.
              </Text>
              <Text kind="body">
                Deactivated team data can be accessed or reactivated from this
                specific location of your Teamspace and/or Team Settings.{' '}
                <Link
                  fontSize={theme.fontSizes[2]}
                  target="_blank"
                  href="https://community.xometry.com/kb/articles/812-how-can-i-delete-a-team-if-it-s-no-longer-needed"
                >
                  Learn more about archiving teams.{' '}
                </Link>
              </Text>
            </Box>
          </Flex>
          <Flex justifyContent="flex-end" marginTop={theme.space[6]}>
            <>
              <Button
                fontWeight={theme.fontWeights.regular}
                marginRight={theme.space[2]}
                onClick={() => setIsArchivedModalOpen(false)}
              >
                Cancel
              </Button>
              <Button
                kind={theme.kind.primary}
                fontWeight={theme.fontWeights.regular}
                onClick={() => {
                  setConfirmArchivedTeam(true);
                  setIsArchivedModalOpen(false);
                }}
              >
                Deactivate Team
              </Button>
            </>
          </Flex>
        </Modal>
      )}
    </Box>
  );
};

TeamInfoPanel.defaultProps = {
  hasWriteOrgPermission: false,
  isStaff: false,
  org: null,
};

export default TeamInfoPanel;
